import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { PAGINATION_PAGE_SIZE } from '@/components/Pagination';
import { reportsService } from '@/services/reports';
import type { ReportMetrics, ReportViewItem } from '@/types/reports';
import { MetricsTabContent } from './components';

export const MetricsSubPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [metricsPage, setMetricsPage] = useState(1);
  const [metrics, setMetrics] = useState<ReportMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [viewsData, setViewsData] = useState<{ data: ReportViewItem[]; total: number } | null>(null);
  const [viewsLoading, setViewsLoading] = useState(false);

  const loadMetrics = useCallback(async () => {
    if (!id) return;
    setMetricsLoading(true);
    try {
      const data = await reportsService.getMetrics(id);
      setMetrics(data);
    } catch {
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [id]);

  const loadViews = useCallback(async () => {
    if (!id) return;
    setViewsLoading(true);
    try {
      const offset = (metricsPage - 1) * PAGINATION_PAGE_SIZE;
      const res = await reportsService.getViews(id, { limit: PAGINATION_PAGE_SIZE, offset });
      setViewsData({ data: res.data, total: res.total });
    } catch {
      setViewsData(null);
    } finally {
      setViewsLoading(false);
    }
  }, [id, metricsPage]);

  useEffect(() => {
    if (id) loadMetrics();
  }, [id, loadMetrics]);

  useEffect(() => {
    if (id) loadViews();
  }, [id, metricsPage, loadViews]);

  return (
    <MetricsTabContent
      metricsLoading={metricsLoading}
      metrics={metrics}
      viewsData={viewsData}
      viewsLoading={viewsLoading}
      page={metricsPage}
      pageSize={PAGINATION_PAGE_SIZE}
      onPageChange={setMetricsPage}
    />
  );
};
